import { Component, Input, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { ProgramEditorService } from "../program-editor.service";
import { TypesService } from "../../types.service";
import { Subscription } from "rxjs/Subscription";
import { SelectizeComponent } from "../../selectize-component/selectize.component";
import { AutoComplete } from "primeng/primeng";

import { Program, ProgramContactInfo } from "@models/program";
import { ActNowDictionaryItem } from "@models/common";

declare var jQuery: any;
declare var Sifter: any;
@Component({
    templateUrl: "./program-contact-info-editor.component.html",
    selector: "app-contact-info-editor"
})
export class ProgramContactInfoEditorComponent implements OnInit, OnDestroy {
    @Input() reviewing = false;
    public baseColumns = 6;

    @ViewChild("agencySelect") agencySelect: AutoComplete;
    @ViewChild("citySelect") citySelect: AutoComplete;

    // Model
    public model: ProgramContactInfo;
    public dataReadySub: Subscription;

    // Validation
    public nameValid = false;
    public addressValid = false;
    public cityValid = false;
    public zipValid = false;
    public emailValid = false;
    public showErrors = false;
    public showErrorsSub: Subscription;
    // Agency Select Properties
    public localAgency: ActNowDictionaryItem = new ActNowDictionaryItem();
    public agencyResults: ActNowDictionaryItem[];
    public agencySet = false;
    public agencyReady = false;
    public agencySub: Subscription;
    public blockAgencyEvent = false;

    // City Select Properties
    public localCity: ActNowDictionaryItem;
    public cityResults: ActNowDictionaryItem[];
    public cityClass = "";
    public citySet = false;
    public cityReady = false;
    public citySub: Subscription;

    constructor(private editorService: ProgramEditorService, private typeService: TypesService) { }

    ngOnInit(): void {
        if (this.reviewing) {
            this.baseColumns = 12;
        }
        this.editorService.updateShowContactInfoErrors(false);
        this.editorService.updateCurrentTab(1);
        this.getData();
        this.subscribeToShowErrors();
        this.subscribeToSelectValues();
    }

    subscribeToSelectValues(): void {
        if (this.editorService.hasAgencies) {
            this.agencyReady = true;
            this.setAgencyInitValue();
        } else {
            this.agencySub = this.editorService.agencyFetch$.subscribe(x => {
                this.agencyReady = true;
                this.setAgencyInitValue();
            });
        }

        if (this.editorService.hasCities) {
            this.cityReady = true;
            this.setCityInitValue();
        } else {
            this.citySub = this.editorService.cityFetch$.subscribe(x => {
                this.cityReady = true;
                this.setCityInitValue();
            });
        }
    }

    ngOnDestroy(): void {
        this.showErrorsSub.unsubscribe();
    }

    getData(): void {
        let ready = this.editorService.dataReady;
        if (ready) {
            let program = this.editorService.getProgram();
            if (program.contactInfo == undefined) {
                this.model = new ProgramContactInfo();
                program.contactInfo = this.model;
                this.editorService.updateProgram(program);
            } else {
                this.model = program.contactInfo;
            }
            this.validate();
            this.setAgencyInitValue();
            this.setCityInitValue();
            if (this.dataReadySub != undefined) {
                this.dataReadySub.unsubscribe();
            }
        } else {
            this.dataReadySub = this.editorService.dataReady$.subscribe(x => this.getData());
        }
    }


    setAgencyInitValue(): void {
        if (this.model != undefined && this.agencyReady && !this.agencySet) {
            if (this.model.agencyId != undefined || this.model.agencyName != undefined) {
                if (this.agencySub != undefined) {
                    this.agencySub.unsubscribe();
                }
                this.localAgency = {
                    id: this.model.agencyId,
                    text: this.model.agencyName
                };
                this.agencySet = true;
            }
        }
    }

    setCityInitValue(): void {
        if (this.model != undefined && this.cityReady && !this.citySet) {
            if (this.model.cityId != undefined) {
                if (this.citySub != undefined) {
                    this.citySub.unsubscribe();
                }
                let item = this.editorService.cities.find(x => x.id === this.model.cityId);
                this.localCity = {
                    id: item.id,
                    text: item.text
                };
                this.citySet = true;
            }
        }
    }

    subscribeToShowErrors(): void {
        this.showErrorsSub = this.editorService.showContactInfoErrors$.subscribe(x => {
            this.showErrors = x;
            this.updateCityClass();
        });
    }

    updateProgram(): void {
        this.validate();
        let program = this.editorService.getProgram();
        program.contactInfo = this.model;
        this.editorService.updateProgram(program);
    }

    validate(): void {
        this.nameValid = this.stringPopulated(this.model.name);
        this.addressValid = this.stringPopulated(this.model.address);
        this.cityValid = this.model.cityId != undefined && this.model.cityId !== 0;
        this.zipValid = this.stringPopulated(this.model.zipCode);
        this.emailValid = this.stringPopulated(this.model.email);


        if (this.emailValid) {
            let regex = new RegExp([
                "^(([^<>()\\[\\]\\\\.,;:\\s@\"]+",
                "(\\.[^<>()\\[\\]\\\\.,;:\\s@\"]+)",
                "*)|(\".+\"))@((\\[[0-9]{1,3}\\",
                ".[0-9]{1,3}\\.[0-9]{1,3}\\.[",
                "0-9]{1,3}\\])|(([a-zA-Z\\-0-",
                "9]+\\.)+[a-zA-Z]{2,}))$"
            ].join(""));

            this.emailValid = regex.test(this.model.email);
        }

        this.updateCityClass();

        let valid = this.nameValid && this.addressValid && this.cityValid && this.zipValid && this.emailValid;

        this.editorService.updateContactInfoValidity(valid);
    }

    stringPopulated(value: string) {
        if (value == undefined) {
            return false;
        }

        return value.replace(/\s/g, "").length > 0;
    }

    updateCityClass(): void {
        if (this.showErrors && !this.cityValid) {
            this.cityClass = "error";
        } else {
            this.cityClass = "";
        }
    }

    searchAgency(event: any): void {
        this.agencyResults = [];
        if (this.editorService.agencies == undefined) { return; }

        let sifter = new Sifter(this.editorService.agencies);

        let result = sifter.search(event.query, {
            fields: ["text"],
            sort: [{ field: "text", direction: "asc" }],
            limit: 100,
            sort_empty: this.editorService.agencies
        });

        let agencies = result.items.map(x => this.editorService.agencies[x.id]);

        this.agencyResults = [...agencies];
    }

    updateAgency(event: any, create: boolean = false) {
        let value = this.localAgency;
        if (typeof (value) === "string") {
            this.localAgency = {
                id: undefined,
                text: value
            };
        }

        // Prevents blur event from triggering updates that arent needed
        if (this.localAgency.id === this.model.agencyId && this.localAgency.text === this.model.agencyName) {
            return;
        }

        if (this.localAgency.text == undefined || this.localAgency.text === "") {
            this.model.agencyId = undefined;
            this.model.agencyName = undefined;
            this.localAgency.id = undefined;
            this.localAgency.text = undefined;
            create = false;
        } else {
            this.model.agencyId = this.localAgency.id;
            this.model.agencyName = this.localAgency.text;
        }

        this.updateProgram();

        // commenting out the code below because we do not need the agency to be created after any update
        // the agency update/create will be handled on the post of the program

        // if (create && this.localAgency != undefined && this.localAgency.id == undefined) {
        //     this.typeService.createAgency(this.localAgency.text).subscribe(
        //         x => {
        //             this.localAgency = x;
        //             this.model.agencyId = this.localAgency.id;
        //             this.model.agencyName = this.localAgency.text;
        //             this.updateProgram();

        //             let exists = this.editorService.agencies.find(y => y.id === x.id) != undefined;
        //             if (!exists) {
        //                 this.editorService.agencies.push(x);
        //             }
        //         }
        //     );
        // }
    }

    agencyBlur(event: any) {
        setTimeout(() => {
            if (this.blockAgencyEvent) {
                this.blockAgencyEvent = false;
            } else {
                this.updateAgency(event, true);
            }
        }, 200);
    }
    agencySelected(event: any) {
        this.blockAgencyEvent = true;
        this.updateAgency(event);
    }

    agencyClick() {
        console.log("DROP DOWN BLOCK");
        this.blockAgencyEvent = true;
        setTimeout(() => {
            if (this.blockAgencyEvent) {
                console.log("DROP DOWN UNBLOCK");
                this.blockAgencyEvent = false;
            }
        }, 100);
    }

    searchCity(event: any) {
        this.cityResults = [];

        if (this.editorService.cities == undefined) { return; }

        let sifter = new Sifter(this.editorService.cities);

        let result = sifter.search(event.query, {
            fields: ["text"],
            sort: [{ field: "text", direction: "asc" }],
            limit: 100
        });

        let cities = result.items.map(x => this.editorService.cities[x.id]);

        this.cityResults = [...cities];
    }

    updateCity(event: any) {

        this.model.cityId = this.localCity.id;
        this.updateProgram();
    }

    clearCity() {
        this.model.cityId = undefined;
        this.updateProgram();
    }

    checkCityInput() {
        if (this.localCity == null) { return; }
        if (!this.localCity.id) { this.clearCity(); }
    }

}



// WEBPACK FOOTER //
// C:/WORK/Projects/ActNow_NG2/src/app/common/program-editors/contact-info/program-contact-info-editor.component.ts